// Chip: a pill that toggles a filter or an option. On is a tint plus a check that draws itself
// in, so the state is a shape as well as a colour. A chip whose option has a colour of its own
// (a status that is also the calendar's legend) takes it as `hex`, on only.
// docs/operations/design-standard.md, "Controls" and "Colour and contrast".

import React, { useRef } from 'react';
import { IconCheck } from './icons';
import { CONTROL_MOTION, FOCUS, PRESS, STATE, TOUCH, useTicker } from './motion';
import { readable, tint } from './Tag';

export interface ChipProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title' | 'aria-pressed'> {
  pressed: boolean;
  /** A count beside the label, such as how many rows the filter leaves. Ticks when it changes. */
  count?: number;
  /** The option's own colour, where it carries meaning. Shown while on; off stays neutral. */
  hex?: string;
  /** An icon before the label, after the check. */
  leading?: React.ReactNode;
}

const Count: React.FC<{ value: number }> = ({ value }) => (
  <span className="ml-1.5 flex-none text-xs tabular-nums">{useTicker(value)}</span>
);

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ pressed, count, hex, leading, className = '', type = 'button', style, children, ...rest }, ref) => {
    // Counted during render so the remount below replays the draw on each turn-on; chips on at
    // load keep it still, or a filter row would animate itself on arrival.
    const lands = useRef(0);
    const was = useRef(pressed);
    if (was.current !== pressed) {
      was.current = pressed;
      if (pressed) lands.current += 1;
    }
    const lit = pressed && hex ? { backgroundColor: tint(hex, 0.18), color: readable(hex) } : undefined;
    return (
      <button
        ref={ref}
        type={type}
        aria-pressed={pressed}
        style={lit || style ? { ...style, ...lit } : undefined}
        className={`inline-flex h-7 min-w-0 max-w-full flex-none select-none items-center rounded-full px-3 text-[13px] font-medium disabled:pointer-events-none disabled:!bg-surface-container disabled:!text-on-surface-disabled ${STATE} ${CONTROL_MOTION} ${PRESS} ${FOCUS} ${TOUCH} ${
          pressed ? (hex ? '' : 'bg-primary/15 text-primary-ink') : 'bg-surface-container-high text-on-surface-variant hover:text-on-surface'
        } ${className}`}
        {...rest}
      >
        <span
          aria-hidden="true"
          className={`inline-flex flex-none overflow-hidden transition-[width,margin,opacity] duration-standard ease-emphasized motion-reduce:transition-[opacity] ${
            pressed ? '-ml-1 mr-1 w-3.5 opacity-100' : 'ml-0 mr-0 w-0 opacity-0'
          } ${lands.current ? '' : '[&_path]:!animate-none'}`}
        >
          <IconCheck key={lands.current} draw className="h-3.5 w-3.5 flex-none" />
        </span>
        {leading ? <span aria-hidden="true" className="mr-1 inline-flex flex-none">{leading}</span> : null}
        <span className="truncate">{children}</span>
        {/* The space keeps the accessible name "Pending 43", not "Pending43"; flex never draws it. */}
        {count !== undefined && <>{' '}<Count value={count} /></>}
      </button>
    );
  },
);
Chip.displayName = 'Chip';

/** A named row of chips: the name tells a screen reader what the chips filter. */
export const ChipGroup: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({ label, className = '', children }) => (
  <div role="group" aria-label={label} className={`flex min-w-0 flex-wrap items-center gap-1.5 ${className}`}>
    {children}
  </div>
);

/** A hairline between sets of chips in one row (statuses, then kinds, then overlays). */
export const ChipDivider: React.FC = () => <span aria-hidden="true" className="mx-1 h-4 w-px flex-none bg-outline-variant" />;

export default Chip;
